import { Injectable } from '@angular/core';
import { Bubble } from '../models/bubble';

@Injectable({
  providedIn: 'root'
})
export class BubblePhysicsService {
  private spatialGrid = new Map<string, Bubble[]>();
  private readonly CELL_SIZE = 100;
  private hopTimers = new Map<Bubble, any>(); // Таймеры для прыжков

  updatePhysics(bubbles: Bubble[], containerWidth: number, containerHeight: number, settings: any): void {
    // Ограничиваем количество обрабатываемых пузырьков для производительности
    const maxProcessBubbles = Math.min(bubbles.length, settings.maxBubbles || 200);
    const activeBubbles = bubbles.slice(0, maxProcessBubbles);
    
    this.spatialGrid.clear();
    
    // 1. Обновление позиций
    activeBubbles.forEach(bubble => {
      if (!bubble.isPaused && !bubble.isMeeting) {
        bubble.vy += settings.gravity;
        bubble.x += bubble.vx;
        bubble.y += bubble.vy;
      }

      // Обработка случайных прыжков
      if(settings.preset === 'walking') this.handleRandomHops(bubble, settings);
      
      this.handleBoundaries(bubble, containerWidth, containerHeight, settings);
    });

    // 2. Заполнение spatial grid
    this.fillSpatialGrid(activeBubbles);

    // 3. Обработка коллизий (только если пузырьков не слишком много)
    if (settings.collision && activeBubbles.length <= settings.maxBubblesCollision) {
      this.handleCollisions(activeBubbles);
    }
  }

  private handleRandomHops(bubble: Bubble, settings: any): void {
  if (!settings.randomHop || bubble.isHopping || bubble.isMeeting || bubble.isPaused) {
    return;
  }

  // Уменьшаем шанс прыжка для лучшей производительности
  const hopChance = 0.005; // 0.5% вместо 2%

  if (Math.random() < hopChance && !this.hopTimers.has(bubble)) {
    this.initiateRandomHop(bubble, settings);
  }
}


  private initiateRandomHop(bubble: Bubble, settings: any): void {
  bubble.isHopping = true;

  // фиксированная скорость для прыжка ~100px
  bubble.vy = -5;

  const hopDuration = 1500; // мс
  const timer = setTimeout(() => {
    bubble.isHopping = false;
    this.hopTimers.delete(bubble);
  }, hopDuration);

  this.hopTimers.set(bubble, timer);
}


  // Очистка таймеров при уничтожении сервиса
  ngOnDestroy() {
    this.cleanup();
  }

  cleanup() {
    this.hopTimers.forEach((timer, bubble) => {
      clearTimeout(timer);
      bubble.isHopping = false;
    });
    this.hopTimers.clear();
    this.spatialGrid.clear();
  }

  clearBubbleTimers(bubble: Bubble) {
    if (this.hopTimers.has(bubble)) {
      clearTimeout(this.hopTimers.get(bubble));
      this.hopTimers.delete(bubble);
      bubble.isHopping = false;
    }
  }

  private fillSpatialGrid(bubbles: Bubble[]): void {
    bubbles.forEach(bubble => {
      if (bubble.isMeeting) return;
      
      const key = this.getGridKey(bubble.x, bubble.y);
      if (!this.spatialGrid.has(key)) {
        this.spatialGrid.set(key, []);
      }
      this.spatialGrid.get(key)!.push(bubble);
    });
  }

  private getGridKey(x: number, y: number): string {
    const cellX = Math.floor(x / this.CELL_SIZE);
    const cellY = Math.floor(y / this.CELL_SIZE);
    return `${cellX},${cellY}`;
  }

  private getNeighbors(bubble: Bubble): Bubble[] {
    const cellX = Math.floor(bubble.x / this.CELL_SIZE);
    const cellY = Math.floor(bubble.y / this.CELL_SIZE);
    const neighbors: Bubble[] = [];

    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const key = `${cellX + dx},${cellY + dy}`;
        if (this.spatialGrid.has(key)) {
          neighbors.push(...this.spatialGrid.get(key)!);
        }
      }
    }

    return neighbors.filter(b => b !== bubble);
  }

  private handleCollisions(bubbles: Bubble[]): void {
    bubbles.forEach(bubble => {
      if (bubble.isMeeting) return;

      const neighbors = this.getNeighbors(bubble);
      neighbors.forEach(other => {
        if (this.checkCollision(bubble, other)) {
          this.resolveCollision(bubble, other);
        }
      });
    });
  }

  private checkCollision(bubble1: Bubble, bubble2: Bubble): boolean {
    const dx = bubble1.x - bubble2.x;
    const dy = bubble1.y - bubble2.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < bubble1.radius * 2;
  }

  private resolveCollision(bubble1: Bubble, bubble2: Bubble): void {
    const dx = bubble1.x - bubble2.x;
    const dy = bubble1.y - bubble2.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    if (distance === 0) return;

    const nx = dx / distance;
    const ny = dy / distance;

    // Разделяем пузыри
    const overlap = (bubble1.radius * 2) - distance;
    bubble1.x += nx * (overlap / 2);
    bubble1.y += ny * (overlap / 2);
    bubble2.x -= nx * (overlap / 2);
    bubble2.y -= ny * (overlap / 2);

    // Обмен скоростями
    const tempVx = bubble1.vx;
    const tempVy = bubble1.vy;
    bubble1.vx = bubble2.vx;
    bubble1.vy = bubble2.vy;
    bubble2.vx = tempVx;
    bubble2.vy = tempVy;
  }

  private handleBoundaries(bubble: Bubble, containerWidth: number, containerHeight: number, settings: any): void {
    if (bubble.isMeeting) return;

    // Левая и правая границы
    if (bubble.x <= 0 && bubble.vx < 0 && settings.preset !== 'passing') {
      bubble.vx *= -1;
      bubble.x = 0;
    }

    if (bubble.x >= containerWidth - bubble.radius * 2 && bubble.vx > 0 && settings.preset !== 'passing') {
      bubble.vx *= -1;
      bubble.x = containerWidth - bubble.radius * 2;
    }

    // Верхняя граница (потолок)
    let maxY = containerHeight - (settings.ceiling || 900)
    if (bubble.y <= maxY && bubble.vy < 0) {
      bubble.vy = -bubble.vy * 0.8;
      bubble.y = maxY;
    }

    // Нижняя граница (пол) - без отскока для прыгающих пузырьков
    const bottomY = containerHeight - bubble.radius * 2;
    if (bubble.y >= bottomY && bubble.vy > 0) {
      if (bubble.isHopping && settings.preset !== 'floating') {
        // Для прыгающих пузырьков - просто останавливаем у пола
        bubble.vy = 0;
        bubble.y = bottomY;
      } else if (!settings.bounceFloor) {
        bubble.vy = 0;
        bubble.y = bottomY;
      } else {
        bubble.vy = settings.floatMode ? -bubble.vy : -this.randomSpeed(settings) * 2;
        bubble.y = bottomY;
      }
    }
  }

  randomSpeed(settings: any): number {
    if (settings.maxSpeed <= 0) return 1;
    if (settings.maxSpeed === settings.minSpeed) return settings.maxSpeed;

     const magnitude = Math.random() * (settings.maxSpeed - settings.minSpeed) + settings.minSpeed;
    const direction = Math.random() < 0.5 ? -1 : 1;
    return magnitude * direction;
  }
}